<script setup lang="ts">
  import { ref, computed } from 'vue';
  import uniqid from 'uniqid';
  import useTotpStore from '@/common/store/pinia/totp';
  import TwoFactorAuthSetup from '@/common/shared/TwoFactorAuthSetup.vue';
  import { useNotifications } from '@/common/utils/notifications/notificationsPlugin';
  import { useI18n } from '@/common/composables/vue-i18n.composable';

  type EmitEvents = {
    (event: 'close'): void;
  }

  const emit = defineEmits<EmitEvents>();
  const totpStore = useTotpStore();
  const { notify } = useNotifications();
  const { t } = useI18n();

  const setupRef = ref();
  const operationId = computed(() => uniqid('two_factor_auth_'));

  const onClose = () => {
    setupRef.value?.resetForm();
    totpStore.clearError();
    emit('close');
  };

  const handleSubmit = async (code: string) => {
    totpStore.clearError();
    try {
      await totpStore.enableTotp(code);
      notify.success({
        operationId: operationId.value,
        title: t('Two-factor authentication configured'),
        message: t('Two-factor authentication successfully configured')
      });
      onClose();
    } catch (err: any) {
      // Error is set in the store and displayed in the form
    }
  };

  defineExpose({
    handleSubmit,
    onClose,
    setupRef,
    totpStore
  });
</script>

<template>
  <el-dialog
    class="v-two-factor-auth-dialog"
    append-to-body
    :custom-class="'two-factor-auth-dialog'"
    height-size="fixed-auto"
    visible
    width-size="small"
    :show-close="false"
    :close-on-click-modal="false"
    @close="onClose"
  >
    <template #body-header>
      <div class="body-header-title el-text--align-center">
        {{ $t('Configure two-factor authentication') }}
      </div>
    </template>

    <div class="dialog-body pl-24 pr-24 pb-24">
      <TwoFactorAuthSetup
        ref="setupRef"
        @submit="handleSubmit"
        @cancel="onClose"
      />
    </div>
  </el-dialog>
</template>

<style lang="scss" scoped>
::v-deep .two-factor-auth-dialog .el-dialog__body {
  background-color: $av-fixed-white;
}

.body-header-title {
  font-size: $av-font-size-l;
  padding: 24px 24px 16px;
}
</style>
